import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout, { scoreColor, eligBadge } from "./AdminLayout";
import api, { API } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { STATUS_LABELS, COUNTIES, LICENCES } from "../../lib/options";
import { Search, Download, Filter } from "lucide-react";
import { toast } from "sonner";

export default function Candidates() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [f, setF] = useState({ sort: "highest_match" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { sort: f.sort };
    if (search) params.search = search;
    if (f.county) params.county = f.county;
    if (f.driving_licence) params.driving_licence = f.driving_licence;
    if (f.eligibility) params.eligibility = f.eligibility;
    if (f.status) params.status = f.status;
    if (f.ethnic_food) params.ethnic_food = true;
    if (f.min_score) params.min_score = f.min_score;
    if (f.min_experience) params.min_experience = f.min_experience;
    const { data } = await api.get("/admin/candidates", { params });
    setRows(data.candidates); setCount(data.count);
  }, [search, f]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async (status) => {
    try {
      const res = await api.get("/admin/export", { params: { status }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `kera_${status}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const setFilter = (k, v) => setF((prev) => ({ ...prev, [k]: v === "__all" ? undefined : v }));

  return (
    <AdminLayout title="Candidate Database" actions={
      <Button data-testid="export-shortlisted-button" onClick={() => exportCsv("shortlisted")} className="bg-[#C79A3B] text-[#101820] font-semibold rounded-full"><Download className="h-4 w-4 mr-1" /> Export Shortlisted</Button>
    }>
      <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="candidate-table-search-input" placeholder="Search name, company, job title, skills, CV text…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#FAF6EE]" />
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">{count} candidate(s)</span>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <Filter className="h-4 w-4 text-[#C79A3B]" />
          <FilterSelect testid="candidate-table-filter-county" value={f.county} onChange={(v) => setFilter("county", v)} placeholder="County" options={COUNTIES} />
          <FilterSelect testid="candidate-table-filter-status" value={f.status} onChange={(v) => setFilter("status", v)} placeholder="Status" options={Object.keys(STATUS_LABELS).filter(s=>s!=="draft")} labels={STATUS_LABELS} />
          <FilterSelect testid="candidate-table-filter-eligibility" value={f.eligibility} onChange={(v) => setFilter("eligibility", v)} placeholder="Eligibility" options={["eligible","review","not_eligible"]} labels={{eligible:"Eligible",review:"Needs Review",not_eligible:"Not Eligible"}} />
          <FilterSelect testid="candidate-table-filter-licence" value={f.driving_licence} onChange={(v) => setFilter("driving_licence", v)} placeholder="Licence" options={LICENCES} />
          <FilterSelect testid="candidate-table-filter-experience" value={f.min_experience} onChange={(v) => setFilter("min_experience", v)} placeholder="Min experience" options={["3","5","8"]} labels={{"3":"3+ yrs","5":"5+ yrs","8":"8+ yrs"}} />
          <label className="flex items-center gap-2 text-sm text-[#101820]"><Checkbox data-testid="filter-ethnic-food" checked={!!f.ethnic_food} onCheckedChange={(v) => setFilter("ethnic_food", v ? true : "__all")} /> Ethnic food exp.</label>
          <div className="ml-auto">
            <FilterSelect testid="candidate-table-sort" value={f.sort} onChange={(v) => setFilter("sort", v)} placeholder="Sort" options={["highest_match","newest","oldest","most_experience","most_ethnic"]} labels={{highest_match:"Highest Match",newest:"Newest",oldest:"Oldest",most_experience:"Most Experience",most_ethnic:"Most Ethnic Food Exp"}} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#101820] text-[#E4C675] text-left text-xs uppercase tracking-wider">
              {["Candidate","Location","Current Role","Yrs Sales","Ethnic Food","Irish Mkt","Licence","Eligibility","Applied","Match","Status"].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#C79A3B]/10">
            {rows.map((r) => (
              <tr key={r.application_id} data-testid={`candidate-row-${r.application_id}`} onClick={() => navigate(`/admin/candidates/${r.application_id}`)} className="hover:bg-[#FAF6EE] cursor-pointer">
                <td className="px-4 py-3"><div className="font-semibold text-[#101820]">{r.name}</div><div className="text-xs text-muted-foreground font-mono-data">{r.reference}</div></td>
                <td className="px-4 py-3 whitespace-nowrap">{r.location || "—"}</td>
                <td className="px-4 py-3"><div>{r.current_role || "—"}</div><div className="text-xs text-muted-foreground">{r.current_employer}</div></td>
                <td className="px-4 py-3 text-center">{r.years_sales}</td>
                <td className="px-4 py-3 text-center">{r.ethnic_food_years}y</td>
                <td className="px-4 py-3 text-center">{r.irish_market}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">{r.driving_licence}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${eligBadge(r.eligibility_status)}`}>{r.eligibility_status === "eligible" ? "Eligible" : r.eligibility_status === "review" ? "Review" : "Not Eligible"}</span></td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">{r.application_date ? new Date(r.application_date).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3"><span className={`inline-block font-mono-data font-bold text-sm px-2.5 py-1 rounded-md border ${scoreColor(r.match_score)}`}>{r.match_score}{r.is_overridden && "*"}</span></td>
                <td className="px-4 py-3"><span className="text-xs font-medium">{STATUS_LABELS[r.status]}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-muted-foreground">No candidates match your filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">* indicates a recruiter-overridden score.</p>
    </AdminLayout>
  );
}

function FilterSelect({ value, onChange, placeholder, options, labels, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-auto min-w-[130px] bg-[#FAF6EE] h-9 text-sm"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">{placeholder}: All</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{labels ? labels[o] : o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
